/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { Popover, OverlayTrigger } from 'react-bootstrap'
import moment from 'moment'
import DatePicker from '@mdsol/lego/lib/DatePicker'
import type { MetadataElement, FilePropertiesDetail } from '../../../services/api/files'
import type { SelectOption } from '../../../utils/selectOptions'
import { checkIsPropertyValueMissing, isPropertyRequired, MetadataType } from '../../../utils/propertyValidation'
import { FsiPropertiesInlineEditor } from './FsiPropertiesInlineEditor'
import { Select } from '../../Select/Select'
import { convertTimestampToUTCMoment } from '../../../utils/dateUtils'
import { transformMetadataOptions, transformToSelectOptions } from '../../../utils/selectOptions'
import { isCheckmated } from '~/services/checkmate'
import { getMomentDateFormat, getDatePickerParseFormats } from '~/utils/dateFormatting'

interface PropertiesInfo {
  noProperties?: boolean
  isInCR?: boolean
  isTemplate?: boolean
  isMultiFile?: boolean
  containsMultiUser?: boolean
  containsMultiGroup?: boolean
  containsMixedValues?: boolean
  isMDF?: boolean
  MDFType?: string
  isDocument?: boolean
  [key: string]: any
}

interface FsiPropertiesTableProps {
  showJustAcls?: boolean
  propertiesDetails: FilePropertiesDetail
  assembledMetadataCopy: MetadataElement[]
  assembledMetadata: MetadataElement[]
  propertiesInfo: PropertiesInfo
  propertiesAclsIds: React.MutableRefObject<number[] | null>
  propertiesPlatformIds?: React.MutableRefObject<number[] | null>
  userOptions: SelectOption[] | null
  groupOptions: SelectOption[] | null
  groupOptionsCaRolesDisabled: SelectOption[] | null
  mapValueToObject: (property: MetadataElement, value: any) => any
  errors: {
    requiredPropsAreMissing: boolean
  }
  isEditable?: boolean
  onEditItem?: (property: MetadataElement) => void
  onChangeItem: (property: MetadataElement, value: any, isReturnToInherited?: boolean) => void
  onCancelItem: (property: MetadataElement) => void
  onAcceptItem: (property: MetadataElement) => void
  onRevertItem: (property: MetadataElement) => void
}

export function FsiPropertiesTable({
  showJustAcls = false,
  propertiesDetails,
  assembledMetadataCopy,
  assembledMetadata,
  propertiesInfo,
  propertiesAclsIds,
  propertiesPlatformIds,
  userOptions,
  groupOptions,
  groupOptionsCaRolesDisabled,
  mapValueToObject,
  errors,
  isEditable = true,
  onEditItem,
  onChangeItem,
  onCancelItem,
  onAcceptItem,
  onRevertItem
}: FsiPropertiesTableProps) {
  const { t } = useTranslation()

  const popoverMixedValues = (
    <Popover id="multiple-files-popup-info">
      <Popover.Header>{t('MIXED_VALUES_TOOLTIP_TITLE')}</Popover.Header>
      <Popover.Body>
        <div dangerouslySetInnerHTML={{ __html: t('MIXED_VALUES_TOOLTIP_TXT') }}></div>
      </Popover.Body>
    </Popover>
  )

  // Filter properties based on various conditions
  const filteredProperties = assembledMetadata.filter((property, idx) => {
    // Check CR editability (should only filter out if editableInCR is false)
    if (propertiesInfo.isInCR && property.template && property.template.editableInCR === false) {
      return false
    }

    // Check platform property in Checkmate mode
    if (isCheckmated() && property.isPlatformProperty) {
      return false
    }

    // Check MDF ACL filtering
    if (propertiesInfo.isMDF) {
      const isAclProperty = propertiesAclsIds.current?.includes(property.metadataTemplateId) ?? false
      if (showJustAcls && !isAclProperty) {
        return false
      }
      if (!showJustAcls && isAclProperty) {
        return false
      }
    }

    return true
  })

  return propertiesDetails &&
    // propertiesDetails.canEditProperties &&
    !propertiesInfo.emptyProperties &&
    assembledMetadata.length ? (
    <table className="metadatatable" style={{ border: 'none' }}>
      <thead>
        <tr>
          <th className="name">{t('NAME')}</th>
          <th className="value">
            <span>{t('VALUE')}</span>
            {propertiesInfo.containsMixedValues && (
              <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popoverMixedValues}>
                <span className="info_hover"></span>
              </OverlayTrigger>
            )}
          </th>
        </tr>
      </thead>

      <tbody>
        {filteredProperties.map((property, propIndex) => {
          const required = isPropertyRequired(property)
          const hasError = required && checkIsPropertyValueMissing(property)

          return (
            <tr key={property.metadataTemplateId + '_' + propIndex} className="new">
              <td
                className={
                  'property-name' +
                  (hasError ? ' has-error' : '') +
                  (property.requiredOnFSITemplate ? ' required' : '') +
                  (propertiesInfo.isDocument ? ' documentWidth' : '')
                }
                title={property.template?.name}
              >
                <span>{property.template?.name}</span>
              </td>
              <td
                className={
                  (hasError ? ' has-error' : '') +
                  (required ? ' required' : '') +
                  (propertiesInfo.isDocument ? ' documentWidth' : '')
                }
              >
                {renderPropertyInput(property, propIndex, {
                  propertiesDetails,
                  propertiesInfo,
                  assembledMetadataCopy,
                  userOptions,
                  groupOptions,
                  groupOptionsCaRolesDisabled,
                  mapValueToObject,
                  required,
                  onAcceptItem,
                  onRevertItem,
                  onCancelItem,
                  onChangeItem,
                  t
                })}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  ) : (
    <></>
  )
}

// Helper function to render property input based on type
function renderPropertyInput(
  property: MetadataElement,
  propIndex: number,
  context: {
    propertiesDetails: FilePropertiesDetail
    propertiesInfo: PropertiesInfo
    assembledMetadataCopy: MetadataElement[]
    userOptions: SelectOption[] | null
    groupOptions: SelectOption[] | null
    groupOptionsCaRolesDisabled: SelectOption[] | null
    mapValueToObject: (property: MetadataElement, value: any) => any
    required: boolean
    onAcceptItem: (property: MetadataElement) => void
    onRevertItem: (property: MetadataElement) => void
    onCancelItem: (property: MetadataElement) => void
    onChangeItem: (property: MetadataElement, value: any) => void
    t: (key: string) => string
  }
) {
  const {
    propertiesDetails,
    propertiesInfo,
    assembledMetadataCopy,
    userOptions,
    groupOptions,
    groupOptionsCaRolesDisabled,
    mapValueToObject,
    required,
    onAcceptItem,
    onRevertItem,
    onCancelItem,
    onChangeItem,
    t
  } = context

  const type = property.template?.type

  // Helper function to get transformed options for Select components
  const getSelectOptions = (): SelectOption[] => {
    // First check if reactOptions already exists
    if (property.template?.definition?.reactOptions) {
      return property.template.definition.reactOptions
    }

    // If not, transform the raw options
    if (property.template?.definition?.options) {
      return transformToSelectOptions(property.template.definition.options, 'id', 'name')
    }

    return []
  }

  switch (type) {
    case MetadataType.DATETIME:
      const DatePickerComponent = DatePicker as any
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          value={() => {
            if (!property.value) return null
            const dateMoment = moment.isMoment(property.value)
              ? property.value
              : convertTimestampToUTCMoment(property.value)
            return dateMoment.format(getMomentDateFormat())
          }}
          input={
            <DatePickerComponent
              name={`property_${propIndex}`}
              selected={
                property.value ? (moment.isMoment(property.value) ? property.value : moment(property.value)) : null
              }
              placeholderText={getMomentDateFormat()}
              onChange={(val: any) => {
                onChangeItem(property, val)
              }}
              popperProps={{ positionFixed: true }}
              popoverTargetOffset="3px 0"
              todayButton="Today"
              dateFormatCalendar="MMMM"
              dateFormat={getDatePickerParseFormats()}
              showYearDropdown
              className="form-control field-control"
            />
          }
        />
      )

    case MetadataType.STRING:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          value={property.value}
          input={
            <input
              name={`property_${propIndex}`}
              value={property.value || ''}
              type="text"
              className="editable-input form-control"
              onChange={(event) => {
                onChangeItem(property, event.target.value)
              }}
            />
          }
        />
      )

    case MetadataType.NUMBER:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          value={property.value}
          input={
            <input
              value={property.value || ''}
              name={`property_${propIndex}`}
              type="number"
              className="editable-input form-control"
              onPaste={(e) => {
                e.preventDefault()
              }}
              onKeyDown={(e) => {
                // Allow only numbers, arrows, backspace, delete
                const allowedKeys = [8, 37, 38, 39, 40, 46]
                const isNumber = (e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)
                if (!isNumber && !allowedKeys.includes(e.keyCode)) {
                  e.preventDefault()
                }
              }}
              onChange={(e) => {
                onChangeItem(property, e.target.value)
              }}
            />
          }
        />
      )

    case MetadataType.SELECT_ONE:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          input={
            <Select
              value={property.value}
              name={`property_${propIndex}`}
              placeholder={t('ITEM(S)...')}
              autoFocus
              options={getSelectOptions()}
              multi={false}
              isSearchable
              isClearable={!required}
              onChange={(option: any) => {
                onChangeItem(property, option ? option.value : null)
              }}
            />
          }
        />
      )

    case MetadataType.SELECT_MULTI:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          value={() => {
            return property.value ? mapValueToObject(property, property.value) : []
          }}
          input={
            <Select
              value={property.value}
              name={`property_${propIndex}`}
              placeholder={t('ITEM(S)...')}
              autoFocus
              options={getSelectOptions()}
              multi={true}
              isSearchable
              isClearable={!required}
              onChange={(options: any) => {
                const newVal = options ? options.map((opt: any) => opt.value) : []
                onChangeItem(property, newVal)
              }}
            />
          }
        />
      )

    case MetadataType.ARTIFACT:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          value={property.value}
          input={
            <Select
              value={property.value}
              name={`property_${propIndex}`}
              placeholder={t('ITEM(S)...')}
              autoFocus
              options={getSelectOptions()}
              isSearchable
              isClearable={!required}
              onChange={(option: any) => {
                onChangeItem(property, option ? option.value : null)
              }}
              onFocus={() => (property ? onAcceptItem(property) : null)}
            />
          }
        />
      )

    case MetadataType.SELECT_ONE_USER:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertyCopy={assembledMetadataCopy[propIndex]}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          value={() => {
            const user = property.value ? mapValueToObject(property, property.value) : null
            return user ? user.chName || user.name : ''
          }}
          input={
            <Select
              value={property.value}
              name={`property_${propIndex}`}
              placeholder={t('ALL_USERS')}
              options={userOptions || []}
              multi={false}
              isSearchable
              isClearable={!required}
              isDisabled={Boolean(
                property.value && typeof property.value === 'object' && (property.value as any).enabled === false
              )}
              filterOption={(option: any) => {
                return option.data.enabled || (property.value && option.data.id === (property.value as any).id)
              }}
              onChange={(option: any) => {
                onChangeItem(property, option ? option.value : null)
              }}
            />
          }
        />
      )

    case MetadataType.SELECT_MULTI_USER:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertyCopy={assembledMetadataCopy[propIndex]}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          value={() => {
            const users = property.value ? mapValueToObject(property, property.value) : []
            return Array.isArray(users) ? users.map((u: any) => u.chName || u.name) : []
          }}
          input={
            <Select
              value={property.value}
              name={`property_${propIndex}`}
              placeholder={t('ALL_USERS')}
              options={userOptions || []}
              multi={true}
              isSearchable
              isClearable={!required}
              isOptionDisabled={(option: any) => !option.enabled}
              filterOption={(option: any) => {
                return option.data.enabled || (property.value && option.data.id === (property.value as any).id)
              }}
              onChange={(options: any) => {
                onChangeItem(property, options ? options.map((opt: any) => opt.value) : [])
              }}
            />
          }
        />
      )

    case MetadataType.SELECT_ONE_GROUP:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertyCopy={assembledMetadataCopy[propIndex]}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          value={() => {
            const group = property.value ? mapValueToObject(property, property.value) : null
            return group ? group.chName || group.name : ''
          }}
          input={
            <Select
              value={property.value}
              placeholder={t('ALL_GROUPS')}
              options={groupOptionsCaRolesDisabled || []}
              multi={false}
              isSearchable
              isClearable={!required}
              name={`property_${propIndex}`}
              onChange={(option: any) => {
                onChangeItem(property, option ? option.value : null)
              }}
            />
          }
        />
      )

    case MetadataType.SELECT_MULTI_GROUP:
      return (
        <FsiPropertiesInlineEditor
          property={property}
          propertyCopy={assembledMetadataCopy[propIndex]}
          propertiesInfo={propertiesInfo}
          onAccept={onAcceptItem}
          onRevert={onRevertItem}
          onCancel={onCancelItem}
          isEditable={propertiesDetails.canEditProperties}
          isRequired={required}
          isRevertDisabled={
            Array.isArray(property.value) &&
            Array.isArray(groupOptionsCaRolesDisabled) &&
            property.value.some((id: any) => groupOptionsCaRolesDisabled.some((opt) => opt.value === id && opt.isFixed))
          }
          value={() => {
            const groups = property.value ? mapValueToObject(property, property.value) : []
            return Array.isArray(groups) ? groups.map((g: any) => g.chName || g.name) : []
          }}
          input={
            <Select
              value={property.value}
              placeholder={t('ALL_GROUPS')}
              options={groupOptionsCaRolesDisabled || []}
              multi={true}
              isSearchable
              isClearable={!required}
              name={`property_${propIndex}`}
              onChange={(options: any) => {
                onChangeItem(property, options ? options.map((opt: any) => opt.value) : [])
              }}
            />
          }
        />
      )

    default:
      return <span>Unsupported property type: {type}</span>
  }
}

export default FsiPropertiesTable
